import type { ReactElement } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router";
import { AppLayout } from "./shell/AppLayout";
import { RequireAuth } from "./auth/RequireAuth";
import { DashboardView } from "./dashboard/DashboardView";
import { AnalyticsDashboardTestHarness } from "./dashboard/AnalyticsDashboardTestHarness";
import { DocumentsView } from "./views/DocumentsView";
import { NotFoundView } from "./views/NotFoundView";
import { UsersView } from "./views/UsersView";
import { UiKitShowcase } from "./showcase/UiKitShowcase";

/**
 * Redirects `/` to `/users`, preserving the current hash and search.
 *
 * MSAL's `redirectUri` is `window.location.origin` (no path — ISSUE-329
 * §Tunable parameters, one artifact works on every origin), so Entra always
 * returns the browser to `/` with the auth response in the URL hash
 * (`response_mode=fragment`). A bare `<Navigate to="/users" replace />`
 * would drop that hash on its `history.replaceState` call, racing
 * `MsalProvider`'s own async `handleRedirectPromise()` — the router could
 * win the race and silently strip the code before MSAL ever reads it. This
 * carries the hash/search forward so MSAL still finds them regardless of
 * which one runs first.
 */
export function RedirectToUsers(): ReactElement {
  const location = useLocation();
  return <Navigate to={{ pathname: "/users", search: location.search, hash: location.hash }} replace />;
}

/**
 * The route table.
 *
 * Client-side routing only — this app is a static artifact behind CloudFront,
 * so every deep link is served the same `index.html` and resolved here (the
 * hosting side of that contract is the web-admin CDK stack's 403/404 →
 * `/index.html` mapping, ISSUE-328).
 *
 * `/dashboard`, `/users`, and `/documents` are the protected data routes
 * (ISSUE-329 Deliverable C, `/dashboard` added by ISSUE-352): `RequireAuth`
 * renders the sign-in view instead of the data view for a signed-out
 * visitor. `/ui-kit` stays unguarded — it is a development-only
 * design-system surface, never part of the shipped bundle's routes, and
 * carries no data of its own.
 */
export function App(): ReactElement {
  return (
    <Routes>
      <Route element={<AppLayout />}>
        <Route path="/" element={<RedirectToUsers />} />
        <Route
          path="/dashboard"
          element={
            <RequireAuth>
              <DashboardView />
            </RequireAuth>
          }
        />
        <Route
          path="/users"
          element={
            <RequireAuth>
              <UsersView />
            </RequireAuth>
          }
        />
        <Route
          path="/documents"
          element={
            <RequireAuth>
              <DocumentsView />
            </RequireAuth>
          }
        />
        {/* Design-system surface: development only, never part of the shipped bundle's routes. */}
        {import.meta.env.DEV ? <Route path="/ui-kit" element={<UiKitShowcase />} /> : null}
        {import.meta.env.DEV ? <Route path="/test/dashboard" element={<AnalyticsDashboardTestHarness />} /> : null}
        <Route path="*" element={<NotFoundView />} />
      </Route>
    </Routes>
  );
}
